import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { CourseCard } from "../components/CourseCard";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Pill, Video, FileText, ShieldCheck, Smartphone, ArrowRight, Star } from "lucide-react";

export default function Landing() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((res) => setCourses(res.data.slice(0, 4))).catch(() => {});
  }, []);

  const features = [
    { icon: Video, title: "HD Video Lessons", desc: "Structured video modules by expert pharmacy faculty." },
    { icon: FileText, title: "Downloadable PDFs", desc: "High-yield notes and study material for every lesson." },
    { icon: Smartphone, title: "Simple UPI Payment", desc: "Pay via any UPI app, upload proof, get instant access on approval." },
    { icon: ShieldCheck, title: "Free & Paid Courses", desc: "Start free, upgrade to premium GPAT/NIPER prep anytime." },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold uppercase tracking-widest mb-6">
              <Pill className="h-3.5 w-3.5" /> For Pharmacy Students
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Master Pharmacy with <span className="text-teal-700">EZ Education</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
              Free and premium courses in Pharmacology, Pharmaceutics, Clinical Pharmacy & GPAT prep. Learn with video lessons and downloadable notes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/courses">
                <Button data-testid="hero-browse-button" size="lg" className="bg-teal-700 hover:bg-teal-600 text-white gap-2 h-12 px-7">
                  Browse Courses <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="hero-join-button" size="lg" variant="outline" className="h-12 px-7 border-slate-300">
                  Join Free
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
              <div className="flex text-amber-500">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-500" />)}</div>
              Trusted by thousands of pharmacy students
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <img
              src="https://images.unsplash.com/photo-1657551856874-d492ef8ecba0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Pharmacy student"
              className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 hidden sm:block">
              <p className="text-2xl font-extrabold text-teal-700" style={{ fontFamily: "Outfit, sans-serif" }}>50+</p>
              <p className="text-xs text-slate-500 font-medium">Video Lessons</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="rounded-2xl bg-white border border-slate-200 p-6"
            >
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-teal-50 text-teal-700 mb-4">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Popular courses */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-700">Popular Now</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Featured Courses
            </h2>
          </div>
          <Link to="/courses" className="text-sm font-semibold text-teal-700 hover:text-teal-600 flex items-center gap-1">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {courses.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <span className="flex items-center gap-2 font-semibold text-slate-700"><Pill className="h-4 w-4 text-teal-700" /> EZ Education</span>
          <span>© 2026 EZ Education. Empowering pharmacy students.</span>
        </div>
      </footer>
    </div>
  );
}
